import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { TableStateContext } from '../../contexts/tableState/TableStateContext';
import useApi from '../../utils/useApi';
import AddRoundedIcon from '@material-ui/icons/AddRounded';
import { endpoints } from '../../utils/endpoints';
import Table from '../../components/TableWithState/Table/Table';
import { Box, Button, CircularProgress, Typography } from '@material-ui/core';
import { Cell, Column } from 'react-table';
import { NSTable } from '../../types/interfaces/NSTable';
import TablePaging from '../../components/TableWithState/TablePaging/TablePaging';
import { useMemoOne } from 'use-memo-one';
import useStyle from '../../components/TableWithState/TableWithState.style';
import { ITableData } from '../../types/interfaces/ITableData';
import { useHistory } from 'react-router-dom';
import { ETables } from '../../types/enums/ETables';
import { useTranslation } from 'react-i18next';
import tPaths from '../../utils/tPaths';
import { downloadFile } from '../../utils/downloadUtil';
import { config } from '../../constants/config';
import { themeColors } from '../../styles/themeColors';
import GetAppIcon from '@material-ui/icons/GetApp';
import { EPaths } from '../../types/enums/EPaths';

interface IProps {
  tableName: ETables;
  dataFormatter: (data: any) => NSTable.TData;
  cellStyleHandler?: (cell: Cell<any>) => any;
  columns: Column<any>[];
  exportable?: boolean;
  refreshable?: boolean;
  addable?: boolean;
  onAddClick?: () => void;
  refreshTrigger?: number;
  listPath?: EPaths;
  addButtonLabelKey?: string;
  exportFileName?: string;
}

const CruiseShipsTableWithState: React.FC<IProps> = ({
  tableName,
  dataFormatter,
  exportable,
  refreshable,
  addable,
  onAddClick,
  refreshTrigger,
  listPath = EPaths.CruiseShips,
  addButtonLabelKey = tPaths.table.addShip,
  exportFileName = 'cruise-ships',
  ...rest
}) => {
  const styles = useStyle();
  const [totals, setTotals] = useState<{ count: number; pageCount: number }>({ count: 0, pageCount: 0 });

  const { setPage, initTableState, state } = useContext(TableStateContext);
  const { t } = useTranslation();

  const requestProps = useMemoOne(
    () => ({
      tableName,
      page: state[tableName]?.page || 1,
      sort: state[tableName]?.sorting,
      deleteId: state[tableName]?.deleting,
      filters: state[tableName]?.filtering,
    }),
    [state, tableName],
  );

  const endpoint = endpoints.table(requestProps);
  const { data, get, isFetching, keyForSwr, mutate } = useApi<ITableData>(endpoint);
  const history = useHistory();

  const tableData = useMemo(() => data?.table && dataFormatter(data.table), [data, dataFormatter]);

  useEffect(() => {
    if (data) {
      setTotals({ count: data.total || 0, pageCount: data.totalPage || 0 });
    }
  }, [data]);

  useEffect(() => initTableState(tableName), [initTableState, tableName]);

  useEffect(() => {
    if (refreshTrigger) {
      mutate();
    }
  }, [refreshTrigger, mutate]);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(tableName, page);
    },
    [setPage, tableName],
  );

  const handleCellClick = useCallback(
    (cell: Cell<any>) => {
      if (cell.column.id === 'action') return;
      history.push(`${listPath}/${cell.row.original._id}`);
    },
    [history, listPath],
  );

  const handleExportClick = useCallback(
    () =>
      get({
        endpoint: endpoints.exportTable({ tableName, sort: requestProps.sort, filter: requestProps.filters }),
        onSuccess: ({ data: blobData }) => downloadFile(blobData, { name: exportFileName, extension: 'xlsx' }),
        options: { responseType: 'blob' },
      }),
    [get, requestProps],
  );

  const handleRefreshClick = useCallback(async () => {
    const response = await get({ endpoint: endpoints.table({ ...requestProps, refresh: true }) });
    mutate(keyForSwr, response?.data);
    if (response?.data) {
      setTotals({ count: response.data.total || 0, pageCount: response.data.totalPage || 0 });
    }
  }, [get, keyForSwr, requestProps, mutate]);

  return (
    <Box className={styles.contentContainer}>
      <Box className={styles.headerContainer}>
        <Box display="flex">
          <Typography variant="h4">{t(tPaths.table.table(tableName))}</Typography>
          <Box marginLeft={1} color={themeColors.textComplimentary}>
            <Typography variant="h4">{totals?.count || '0'}</Typography>
          </Box>
        </Box>

        <Box className={styles.toolboxContainer}>
          {exportable && data && (
            <Box>
              <Button onClick={handleExportClick} endIcon={<GetAppIcon />}>
                {t(tPaths.table.downloadXls)}
              </Button>
            </Box>
          )}
          {refreshable && (
            <Box>
              <Button onClick={handleRefreshClick} color="secondary">
                {`${t(tPaths.table.refresh)} ${t(tPaths.table.table(tableName))}`}
              </Button>
            </Box>
          )}
          {addable && onAddClick && (
            <Box>
              <Button onClick={onAddClick} color="primary" endIcon={<AddRoundedIcon />}>
                {t(addButtonLabelKey)}
              </Button>
            </Box>
          )}
        </Box>
      </Box>
      {data && !isFetching ? (
        <Box className={styles.container}>
          <Table tableName={tableName} data={tableData} {...rest} handleCellClick={handleCellClick} />
          <TablePaging
            count={totals.count}
            changePage={handlePageChange}
            rowsPerPage={config.itemsPerPage}
            page={state[tableName]?.page || 1}
          />
        </Box>
      ) : (
        <CircularProgress />
      )}
    </Box>
  );
};

export default CruiseShipsTableWithState;
